import { DynamicFormControl, DynamicFormControlType } from '@waseel-asp/tpa-waseel-lib-staging';
import { API } from "../../../../../../shared/constants/api-url";
import { SYSTEM_CONST } from "../../../../../../shared/constants/system-constants.model";
import { EntityType, ExportKey } from "../../../../../../shared/constants/master-data.enum";
import { getTextboxConfig } from "../../../../../../shared/functions/config-function";

export interface ICountry {
  countryId: string;
  nameEnglish: string;
  nameArabic: string;
  codeEnglish: string;
  codeArabic: string;
  internationalCode: string;
  deleted: boolean;
  createdBy: string;
  createdDate: string;
  lastUpdatedBy: string;
  lastUpdatedDate: string;
  deletedBy: string;
  deletedDate: string;
}
export interface IState {
  stateId: string;
  nameEnglish: string;
  nameArabic: string;
  countryId: string;
  countryNameEnglish: string;
  countryNameArabic: string;
  deleted: boolean;
  createdBy: string;
  createdDate: string;
  lastUpdatedBy: string;
  lastUpdatedDate: string;
  deletedBy: string;
  deletedDate: string;
}

export interface ICity {
  cityId: string;
  nameEnglish: string;
  nameArabic: string;
  stateId: string;
  stateNameEnglish: string;
  stateNameArabic: string;
  countryNameEnglish: string;
  countryNameArabic: string;
  deleted: boolean;
  createdBy: string;
  createdDate: string;
  lastUpdatedBy: string;
  lastUpdatedDate: string;
  deletedBy: string;
  deletedDate: string;
}

export interface IMasterDataList {
  id: string
  name: string
  API: string
  dialogConfig: DynamicFormControl[],
  dependentFields?: IDependentField[],
  entityType?: EntityType,
  primaryKey: string,
  exportKey?: string,
}

interface IDependentField {
  API: string,
  name: string,
  text: string | number,
  value: string | number
}

const entityDialogConfig: DynamicFormControl[] = [
  {
    control: getTextboxConfig('FORM_FIELD.ENGLISH_NAME', 'nameEnglish'),
    type: DynamicFormControlType.Text,
    isRequired: true
  },
  {
    control: getTextboxConfig('FORM_FIELD.ARABIC_NAME', 'nameArabic'),
    type: DynamicFormControlType.Text,
    isRequired: true
  }
];

const countryDialogConfig: DynamicFormControl[] = [
  {
    control: getTextboxConfig('FORM_FIELD.ENGLISH_NAME', 'nameEnglish'),
    type: DynamicFormControlType.Text,
    isRequired: true
  },
  {
    control: getTextboxConfig('FORM_FIELD.ARABIC_NAME', 'nameArabic'),
    type: DynamicFormControlType.Text,
    isRequired: true
  },
  {
    control: getTextboxConfig('FORM_FIELD.ENGLISH_CODE', 'codeEnglish'),
    type: DynamicFormControlType.Text,
    isRequired: true
  },
  {
    control: getTextboxConfig('FORM_FIELD.ARABIC_CODE', 'codeArabic'),
    type: DynamicFormControlType.Text,
    isRequired: true
  },
  {
    control: getTextboxConfig('FORM_FIELD.INTERNATIONAL_CODE', 'internationalCode'),
    type: DynamicFormControlType.Text,
    isRequired: true
  },
];

const categoryDialogConfig: DynamicFormControl[] = [
  {
    control: getTextboxConfig('FORM_FIELD.ENGLISH_NAME', 'nameEnglish'),
    type: DynamicFormControlType.Text,
    isRequired: true
  },
  {
    control: getTextboxConfig('FORM_FIELD.ARABIC_NAME', 'nameArabic'),
    type: DynamicFormControlType.Text,
    isRequired: true
  },
  {
    control: {
      label: 'FORM_FIELD.CATEGORY_TYPE',
      formControlName: 'categoryType',
      features: {
        allowClear: true,
        allowMultiple: false
      },
      data: [],
      id: 'categoryType',
      appearance: SYSTEM_CONST.OUTLINE,
      isFloatLabel: true
    },
    type: DynamicFormControlType.DropDown,
    isRequired: true
  }
];

const unitTypeDialogConfig: DynamicFormControl[] = [
  {
    control: getTextboxConfig('FORM_FIELD.ENGLISH_NAME', 'nameEnglish'),
    type: DynamicFormControlType.Text,
    isRequired: true
  },
  {
    control: getTextboxConfig('FORM_FIELD.ARABIC_NAME', 'nameArabic'),
    type: DynamicFormControlType.Text,
    isRequired: true
  },
  {
    control: {
      label: 'CONFIGURATION.MASTER_DATA.UNIT_TYPE.TYPE',
      formControlName: 'type',
      features: {
        allowClear: true,
        allowMultiple: false
      },
      data: [],
      id: 'type',
      appearance: SYSTEM_CONST.OUTLINE,
      isFloatLabel: true
    },
    type: DynamicFormControlType.DropDown,
    isRequired: true
  }
];

export interface IEntity {
  id: string;
  nameEnglish: string;
  nameArabic: string;
  deleted: boolean;
  createdBy: string;
  createdDate: string;
  lastUpdatedBy: string;
  lastUpdatedDate: string;
  deletedBy: string | null;
  deletedDate: string | null;
}

export const masterDataList: IMasterDataList[] = [
  {
    id: EntityType.PolicyStatus,
    name: 'CONFIGURATION.MASTER_DATA.POLICY_STATUS',
    API: API.MASTERDATA.ENTITY,
    dialogConfig: entityDialogConfig,
    entityType: EntityType.PolicyStatus,
    primaryKey: "id",
    exportKey: ExportKey.PolicyStatus
  },
  {
    id: EntityType.RoomType,
    name: 'CONFIGURATION.MASTER_DATA.ROOM_TYPE',
    API: API.MASTERDATA.ENTITY,
    dialogConfig: entityDialogConfig,
    entityType: EntityType.RoomType,
    primaryKey: "id",
    exportKey: ExportKey.RoomType
  },
  {
    id: EntityType.SBSCode,
    name: 'CONFIGURATION.MASTER_DATA.SBSCODE',
    API: API.MASTERDATA.ENTITY,
    dialogConfig: entityDialogConfig,
    entityType: EntityType.SBSCode,
    primaryKey: "id",
    exportKey: ExportKey.SBSCode
  },
  {
    id: EntityType.Category,
    name: 'CONFIGURATION.MASTER_DATA.CATEGORY',
    API: API.MASTERDATA.CATEGORY,
    dialogConfig: categoryDialogConfig,
    primaryKey: "categoryId",
    exportKey: ExportKey.Category
  },
  {
    id: EntityType.ContractStatus,
    name: 'CONFIGURATION.MASTER_DATA.CONTRACT_STATUS',
    API: API.MASTERDATA.ENTITY,
    dialogConfig: entityDialogConfig,
    entityType: EntityType.ContractStatus,
    primaryKey: "id",
    exportKey: ExportKey.ContractStatus
  },
  {
    id: EntityType.BloodGroup,
    name: 'CONFIGURATION.MASTER_DATA.BLOOD_GROUP',
    API: API.MASTERDATA.ENTITY,
    dialogConfig: entityDialogConfig,
    entityType: EntityType.BloodGroup,
    primaryKey: "id",
    exportKey: ExportKey.BloodGroup
  },
  {
    id: EntityType.PreferredLanguage,
    name: 'CONFIGURATION.MASTER_DATA.LANGUAGE',
    API: API.MASTERDATA.ENTITY,
    dialogConfig: entityDialogConfig,
    entityType: EntityType.PreferredLanguage,
    primaryKey: "id",
    exportKey: ExportKey.PreferredLanguage
  },
  {
    id: EntityType.VisaType,
    name: 'CONFIGURATION.MASTER_DATA.VISA_TYPE',
    API: API.MASTERDATA.ENTITY,
    dialogConfig: entityDialogConfig,
    entityType: EntityType.VisaType,
    primaryKey: "id",
    exportKey: ExportKey.VisaType
  },
  {
    id: EntityType.PatientShareType,
    name: 'CONFIGURATION.MASTER_DATA.PATIENT_SHARE_TYPE',
    API: API.MASTERDATA.ENTITY,
    dialogConfig: entityDialogConfig,
    entityType: EntityType.PatientShareType,
    primaryKey: "id",
    exportKey: ExportKey.PatientShareType
  },
  {
    id: EntityType.RoutesOfAdministration,
    name: 'CONFIGURATION.MASTER_DATA.ROUTES_OF_ADMINISTRATION',
    API: API.MASTERDATA.ENTITY,
    dialogConfig: entityDialogConfig,
    entityType: EntityType.RoutesOfAdministration,
    primaryKey: "id",
    exportKey: ExportKey.RoutesOfAdministration
  },
  {
    id: EntityType.CaseType,
    name: 'CONFIGURATION.MASTER_DATA.CASE_TYPE',
    API: API.MASTERDATA.ENTITY,
    dialogConfig: entityDialogConfig,
    entityType: EntityType.CaseType,
    primaryKey: "id",
    exportKey: ExportKey.CaseType
  },
  {
    id: EntityType.DosageForm,
    name: 'CONFIGURATION.MASTER_DATA.DOSAGE_FORM',
    API: API.MASTERDATA.ENTITY,
    dialogConfig: entityDialogConfig,
    entityType: EntityType.DosageForm,
    primaryKey: "id",
    exportKey: ExportKey.DosageForm
  },
  {
    id: EntityType.PackageType,
    name: 'CONFIGURATION.MASTER_DATA.PACKAGE_FORM',
    API: API.MASTERDATA.ENTITY,
    dialogConfig: entityDialogConfig,
    entityType: EntityType.PackageType,
    primaryKey: "id",
    exportKey: ExportKey.PackageType
  },
  {
    id: EntityType.UnitType,
    name: 'CONFIGURATION.MASTER_DATA.UNIT_TYPE',
    API: API.MASTERDATA.UNITTYPE,
    dialogConfig: unitTypeDialogConfig,
    entityType: EntityType.UnitType,
    primaryKey: "unitTypeId",
    exportKey: ExportKey.UnitType
  },
  {
    id: EntityType.StandardName,
    name: 'CONFIGURATION.MASTER_DATA.STANDARD_NAME',
    API: API.MASTERDATA.ENTITY,
    dialogConfig: entityDialogConfig,
    entityType: EntityType.StandardName,
    primaryKey: "id",
    exportKey: ExportKey.StandardName
  },
  {
    id: EntityType.Country,
    name: 'CONFIGURATION.MASTER_DATA.COUNTRIES',
    API: API.MASTERDATA.COUNTRIES,
    dialogConfig: countryDialogConfig,
    primaryKey: "countryId",
    exportKey: ExportKey.Country
  },
  {
    id: EntityType.State,
    name: 'CONFIGURATION.MASTER_DATA.STATES',
    API: API.MASTERDATA.STATE,
    dialogConfig: [],
    dependentFields: [{ API: API.MASTERDATA.COUNTRIES, name: 'countryId', text: 'nameEnglish', value: 'countryId' }],
    primaryKey: "stateId"
  },
  {
    id: EntityType.City,
    name: 'CONFIGURATION.MASTER_DATA.CITIES',
    API: API.MASTERDATA.CITY,
    dialogConfig: [],
    primaryKey: "cityId"
  }
];

const baseConfig = {
  nameEnglish: 'FORM_FIELD.ENGLISH_NAME',
  nameArabic: 'FORM_FIELD.ARABIC_NAME'
};

export const policyStatusConfig = { ...baseConfig, entityName: 'CONFIGURATION.MASTER_DATA.POLICY_STATUS.TITLE' };

export const roomTypeConfig = { ...baseConfig, entityName: 'CONFIGURATION.MASTER_DATA.ROOM_TYPE.TITLE' };

export const sbsCodeConfig = { ...baseConfig, entityName: 'CONFIGURATION.MASTER_DATA.SBSCODE.TITLE' };

export const contractStatusConfig = { ...baseConfig, entityName: 'CONFIGURATION.MASTER_DATA.CONTRACT_STATUS.TITLE' };

export const bloodGroupConfig = { ...baseConfig, entityName: 'CONFIGURATION.MASTER_DATA.BLOOD_GROUP.TITLE' };

export const visaTypeConfig = { ...baseConfig, entityName: 'CONFIGURATION.MASTER_DATA.VISA_TYPE.TITLE' };

export const patientShareConfig = { ...baseConfig, entityName: 'CONFIGURATION.MASTER_DATA.PATIENT_SHARE_TYPE.TITLE' };

export const routesOfAdministrationConfig = { ...baseConfig, entityName: 'CONFIGURATION.MASTER_DATA.ROUTES_OF_ADMINISTRATIONE.TITLE' };

export const caseTypeConfig = { ...baseConfig, entityName: 'CONFIGURATION.MASTER_DATA.CASE_TYPE.TITLE' };

export const dosageFormConfig = { ...baseConfig, entityName: 'CONFIGURATION.MASTER_DATA.DOSAGE_FORM.TITLE' };

export const packageTypeConfig = { ...baseConfig, entityName: 'CONFIGURATION.MASTER_DATA.PACKAGE_FORM.TITLE' };

export const unitTypeConfig = { ...baseConfig, type: 'TYPE', entityName: 'CONFIGURATION.MASTER_DATA.UNIT_TYPE.TITLE' };

export const countryConfig = { ...baseConfig, internationalCode: 'FORM_FIELD.INTERNATIONAL_CODE', codeEnglish: 'FORM_FIELD.ENGLISH_CODE', codeArabic: 'FORM_FIELD.ARABIC_CODE', entityName: 'CONFIGURATION.MASTER_DATA.COUNTRIES.TITLE' };

export const categoryConfig = { ...baseConfig, categoryType: 'FORM_FIELD.CATEGORY_TYPE', entityName: 'CONFIGURATION.MASTER_DATA.CATEGORY.TITLE' };

export const stateConfig = { ...baseConfig, countryId: 'COMMON.COUNTRY', entityName: 'CONFIGURATION.MASTER_DATA.STATES.TITLE' };

export const cityConfig = { ...baseConfig, countryId: 'COMMON.COUNTRY', stateId: 'COMMON.STATE', entityName: 'CONFIGURATION.MASTER_DATA.CITIES.TITLE' };

export const standardNameConfig = { ...baseConfig, entityName: 'CONFIGURATION.MASTER_DATA.STANDARD_NAME.TITLE' };

export const languageTypeConfig = { ...baseConfig, entityName: 'CONFIGURATION.MASTER_DATA.LANGUAGE.TITLE' };
